import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  TablePagination,
  Card,
  Grid,
  Chip,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import KitchenIcon from '@mui/icons-material/Kitchen';
import { useNavigate } from 'react-router-dom';
import { useGetConsumptionsQuery } from '../../../store/api/inventoryApi';

function ConsumptionsList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');

  const { data, isLoading, refetch } = useGetConsumptionsQuery({
    page: page + 1,
    limit: rowsPerPage,
    search
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            p: 1.5,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
            boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
          }}>
            <KitchenIcon sx={{ color: 'white' }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C3E50' }}>
            Stock Clearance (Consumption)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/inventory/consumptions/new')}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
            boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
            }
          }}
        >
          New Clearance
        </Button>
      </Box>

      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by clearance number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={() => refetch()} color="primary">
                <RefreshIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      </Card>

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#F8F9FA' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Kitchen Location</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Total Cost</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Logged By</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  Loading consumption records...
                </TableCell>
              </TableRow>
            ) : data?.consumptions?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  No clearance records found.
                </TableCell>
              </TableRow>
            ) : (
              data?.consumptions?.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell sx={{ fontWeight: 600, color: '#F57C00' }}>
                    {record.consumptionNumber}
                  </TableCell>
                  <TableCell>
                    {new Date(record.date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<KitchenIcon sx={{ fontSize: '1rem !important' }} />}
                      label={record.location?.name}
                      variant="outlined"
                      size="small"
                      sx={{ borderRadius: 1.5 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {Number(record.totalCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED
                  </TableCell>
                  <TableCell>
                    {record.creator?.firstName} {record.creator?.lastName}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/inventory/consumptions/${record.id}`)}
                        sx={{ color: '#F57C00' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data?.pagination?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
}

export default ConsumptionsList;
